import { useContext, useEffect, useState } from 'react';
import Breadcrumbs from '../components/Breadcrumbs';
import { useAuth } from 'react-oidc-context';
import showToast from '../components/ToastComponent';
import { markNotificationAsRead } from '../service/updateNotificationAsRead';
import Loading from '../components/Loading';
import { clearNotificationInbox } from '../service/clearNotificationInbox';
import CustomModal from '../components/CustomModal';
import {
  NotificationInboxInterface,
  NotificationInboxResponse,
} from '../interface/interface';
import { UserContext } from '../context/userContext';
import moment from 'moment';
import { NOTIFICATION_MODE } from '../constant/constant';

export const NotificationInbox = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<
    Array<NotificationInboxInterface>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [notificationIds, setNotificationIds] = useState<Array<string>>([]);
  const [open, setOpen] = useState<boolean>(false);
  const { inboxItem, setUnReadItems } = useContext(UserContext);

  useEffect(() => {
    if (!user?.access_token) return;
    handleNotificationState(inboxItem);
  }, []);

  const handleNotificationState = (
    notifications: Array<NotificationInboxResponse>,
  ) => {
    const notificationData = notifications.map(
      (notification: NotificationInboxResponse) => ({
        title: notification.header,
        message: notification.content,
        redirectUrl: notification.redirectUrl,
        isRead: notification.is_opened,
        notificationId: notification.id,
        createdDate: notification.createdDate,
      }),
    );
    setNotifications(notificationData);
    setUnReadItems(notificationData.map((n) => n.isRead).includes(false));
  };

  const makeNotificationAsRead = async (
    mode: string,
    notificationId: string,
    isRead: boolean,
    url: string,
  ) => {
    try {
      if (!isRead) {
        setLoading(true);
        const response = await markNotificationAsRead(
          user?.access_token as string,
          mode === NOTIFICATION_MODE.BULK ? notificationIds : [notificationId],
        );
        if (typeof response === 'number') {
          showToast({
            message: 'Unable to update notifications',
            type: 'warning',
          });
          return;
        }
        handleNotificationState(response.data);
        setNotificationIds([]);
      }
      if (url) window.location.href = url;
    } catch (err) {
      console.log('Error while updateing notification as read:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearNotification = async () => {
    try {
      setLoading(true);
      const response = await clearNotificationInbox(
        user?.access_token as string,
        notificationIds,
      );
      if (typeof response === 'number') {
        showToast({
          message: 'Unable to clear notifications',
          type: 'warning',
        });
        return;
      }
      showToast({
        message: 'Notification Cleared Successfully',
        type: 'info',
      });
      handleNotificationState(response?.data);
      setNotificationIds([]);
    } catch (err) {
      console.log('Error while updateing notification as read:', err);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const handleCheckboxChange = (id: string) => {
    setNotificationIds((prev: Array<string>) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (notificationIds.length === notifications.length) {
      setNotificationIds([]);
    } else {
      const allIds = notifications?.map((n) => n.notificationId);
      if (!allIds?.length) {
        showToast({
          message: 'There are no unread notification',
          type: 'warning',
        });
        return;
      }
      setNotificationIds(allIds);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            className: 'mr-3',
            href: '/home',
            children: <>Home</>,
          },
          {
            tag: 'p',
            children: <span>Inbox</span>,
          },
        ]}
      />
      {notificationIds?.length ? (
        <div className="flex justify-end m-4">
          <div
            className={
              'px-2 py-1 rounded-full uppercase text-xs text-white font-semibold bg-secondary-pink-400 me-3 cursor-pointer'
            }
            onClick={() => setOpen(true)}
          >
            Clear
          </div>
          <button
            className={
              'px-2 py-1 rounded-full uppercase text-xs text-white font-semibold bg-primary-blue-500 cursor-pointer'
            }
            onClick={() =>
              makeNotificationAsRead(
                NOTIFICATION_MODE.BULK,
                '',
                notifications?.every((n) => n.isRead),
                '',
              )
            }
          >
            Mark As Read
          </button>
        </div>
      ) : (
        <></>
      )}
      <div className="space-y-10 divide-y divide-gray-900/10 add-user overflow-y-scroll">
        <div className="bg-secondary-pink-50 shadow rounded-xl">
          {notifications?.length ? (
            <div className="flex justify-end px-3 py-1">
              <button
                className="text-primary-blue-500 text-sm underline"
                onClick={handleSelectAll}
              >
                {notificationIds.length === notifications.length
                  ? 'Unselect All'
                  : 'Select All'}
              </button>
            </div>
          ) : (
            <></>
          )}
          <div className="px-4 py-6 sm:px-4 sm:py-1">
            {!notifications?.length ? (
              <div className="text-lg text-primary-blue-500 font-regular text-center">
                You’re all caught up!
              </div>
            ) : (
              notifications?.map((notification: any) => (
                <div
                  key={notification.notificationId}
                  className={`bg-white shadow-md rounded-lg p-3 border border-gray-200 mb-3 cursor-pointer`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-start">
                      <div className="me-3">
                        <input
                          type="checkbox"
                          className="form-checkbox h-3 w-3 text-primary-blue-500 cursor-pointer"
                          onChange={() =>
                            handleCheckboxChange(notification.notificationId)
                          }
                          checked={notificationIds.includes(
                            notification.notificationId,
                          )}
                        />
                      </div>
                      <div>
                        <div
                          className={`flex justify-between items-start gap-2 ${notification.isRead ? 'cursor-not-allowed	opacity-70' : ''}`}
                        >
                          <h3 className="text-sm text-primary-blue-500 font-semibold mb-1 flex-1">
                            {notification.title}
                          </h3>
                          <span className="text-xs text-primary-blue-500 whitespace-nowrap flex-shrink-0 font-semibold">
                            {moment(notification.createdDate).format(
                              'DD-MM-YYYY',
                            )}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-xs text-primary-blue-500 font-regular whitespace-pre-line flex-1 ${notification.isRead ? 'cursor-not-allowed	opacity-70' : ''}`}
                          >
                            {notification.message.replace(/\\n/g, '\n')}
                          </p>
                          <button
                            className="flex-shrink-0 ms-1 px-3 py-1 h-fit rounded-full text-xs text-white font-semibold bg-primary-blue-500 cursor-pointer"
                            onClick={() =>
                              makeNotificationAsRead(
                                NOTIFICATION_MODE.SINGLE,
                                notification.notificationId,
                                notification.isRead,
                                notification.redirectUrl,
                              )
                            }
                          >
                            view
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={open}
        onClose={() => setOpen(false)}
        header={'Would you like to clear the Notification Inbox ?'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleClearNotification()}
            >
              Yes
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setOpen(false)}
            >
              No
            </button>
          </>
        }
        modalSize={'w-full md:w-1/2 xl:w-1/3 max-sm:!p-0'}
      >
        {''}
      </CustomModal>
    </>
  );
};
